import { useContext, Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XCircleIcon } from '@heroicons/react/outline'
import { ThemeContext } from '../theme/ThemeContext'

export type ModalId =
  | 'info'
  | 'new-game'
  | 'new-game-confirm'
  | 'stat'
  | 'about'
  | 'create-puzzle'
  | 'dev'
  | 'rooms'
  | 'championship'
  | 'bantumi'
  | false

export type ModalType = ModalId | [ModalId, ModalId]

type Props = {
  title: string
  subTitle?: string
  subTitleLink?: string // Optional link for subtitle
  children: React.ReactNode
  isOpen: boolean
  handleClose: () => void
  /** If true, clicking backdrop or pressing ESC won't close the modal */
  preventBackdropClose?: boolean
  /** Custom z-index for stacking modals. Default is 1010. Use higher values for overlay modals. */
  zIndex?: number
}

export const BaseModal = ({
  title,
  children,
  isOpen,
  handleClose,
  subTitle = '',
  subTitleLink,
  preventBackdropClose = false,
  zIndex = 1010,
}: Props) => {
  const context = useContext(ThemeContext)
  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className={`${context.theme} base-modal fixed inset-0 overflow-y-auto`}
        style={{ zIndex }}
        onClose={preventBackdropClose ? () => { } : handleClose}
      >
        <div className="flex items-start justify-center min-h-screen py-10 px-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-2 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-sm w-full sm:p-4">
              <div className="absolute right-4 top-4">
                <XCircleIcon
                  className="h-6 w-6 cursor-pointer text-gray-900 dark:text-slate-200"
                  onClick={() => handleClose()}
                />
              </div>
              <div>
                <div className="text-center">
                  {subTitle && (
                    <div className="text-xs absolute top-[4px] left-[4px] font-medium text-gray-900 dark:text-slate-200">
                      {subTitleLink ? (
                        <a
                          href={subTitleLink}
                          className="hover:underline cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault()
                            window.location.href = subTitleLink
                          }}
                        >
                          {subTitle}
                        </a>
                      ) : (
                        subTitle
                      )}
                    </div>
                  )}
                  <Dialog.Title
                    as="h3"
                    className="text-lg leading-6 font-medium text-gray-900 dark:text-slate-200"
                  >
                    {title}
                  </Dialog.Title>
                  <div className="mt-2">{children}</div>
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
